import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { getResolvedTargetName } from '../../router/routing-manager';
import { getLastActiveItem } from '../../utils/case-utils';
import * as actions from '../../store/state/actions/action-creators';
import * as StateManager from '../../store/state/state-manager';
import { processDataToDispatchErrorAction, processViewResponse } from '../../store/state/epics/util';
import { isErrorActionType, promiseHandler } from '../common-helper';
import { CONTAINER_NAMES } from '../../container/container-utils';
import APP_CONSTANTS from '../../constants';
import * as actionType from '../../store/state/actions';
import type { Action, State } from '../../store/state/state-manager';
import type { Actionout } from '../../types';

import { DELETE_CASE_IN_CREATE_STAGE } from './action-creator';
import { executeDeleteCaseInCreateStageRequest } from './async-request';
import { publishCreateStageDeletedEvent } from './events';

const deleteCaseInCreateStage = (action$: Observable<Action>, state$: { value: State }) => {
  let actionMgrID = 0;
  let caseType: string;
  let actionsOut: Actionout[] = [];

  return action$.pipe(
    ofType(DELETE_CASE_IN_CREATE_STAGE),
    map((action: Action) => {
      ({ actionMgrID, caseType } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      return actionInPayload.ignoreCaseDeletion
        ? of({ actionInPayload })
        : executeDeleteCaseInCreateStageRequest(actionInPayload);
    }),
    mergeMap(async (data) => {
      if (data && isErrorActionType(data.type)) {
        const { payload } = data;
        const errorAction = processDataToDispatchErrorAction({ ...data, ...payload, actionMgrID });
        actionsOut.push(errorAction);
        return actionsOut;
      }

      const { actionInPayload } = data;
      const { context, isModalAction } = actionInPayload;
      const containerName = isModalAction ? CONTAINER_NAMES.MODAL : CONTAINER_NAMES.PRIMARY;
      const target = getResolvedTargetName(containerName, context);

      // Update semantic url in case of inline delete
      if (!isModalAction && target) {
        // [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic
        const options = { acName: target, target, context };
        const targetContainerData = state$.value.containers[target];
        const { semanticURL } = getLastActiveItem(targetContainerData);

        await processViewResponse(
          {
            directUrlUpdation: true,
            semanticURL
          },
          actionInPayload,
          options
        );
      }

      if (actionInPayload.isProcessAction && actionInPayload.parentCaseInfo) {
        const openWorkByHandleActionInfo = {
          insKey: actionInPayload.parentCaseInfo.ID,
          className: actionInPayload.parentCaseInfo.content.classID,
          context: actionInPayload.parentContext,
          acTargetName: APP_CONSTANTS.PRIMARY,
          isChild: true
        };

        const openWorkByHandle = {
          type: actionType.OPEN_WORK_BY_HANDLE,
          payload: { ...openWorkByHandleActionInfo }
        };

        actionsOut.push(openWorkByHandle);
      }
      const payload = {
        target,
        containerItemID: context,
        context,
        caseDeleted: true
      };
      actionsOut.push(actions.postProcess(actions.removeContainerItem, payload));
      return actionsOut;
    }),
    map(() => {
      StateManager.dispatch(actionsOut);
      if (promiseHandler(actionsOut, actionMgrID) === APP_CONSTANTS.PROMISE_VALUE.RESOLVE) {
        publishCreateStageDeletedEvent({ caseType });
      }
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default deleteCaseInCreateStage;
